import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { grafikService } from '../../../services/grafikService';

/**
 * Modal: edycja domyslnych godzin pracy lokalizacji per dzien tygodnia.
 * Te godziny pre-fillowane sa w ShiftEditModal przy tworzeniu nowej zmiany.
 *
 * Props:
 *   locationId: int (wymagany)
 *   locationName: string (do wyswietlenia w naglowku)
 *   onClose()
 *   onSaved() - opcjonalnie, po zapisie
 */
const DAYS = [
  { dt: 1, label: 'Poniedzialek' },
  { dt: 2, label: 'Wtorek' },
  { dt: 3, label: 'Sroda' },
  { dt: 4, label: 'Czwartek' },
  { dt: 5, label: 'Piatek' },
  { dt: 6, label: 'Sobota' },
  { dt: 7, label: 'Niedziela' }
];

const DEFAULT_ROWS = DAYS.map(d => ({
  dzien_tygodnia: d.dt,
  godzina_od: '',
  godzina_do: '',
  zamkniete: false
}));

const DEFAULT_PUNKT = {
  tolerancja_spoznienie_min: 5,
  tolerancja_wczesniej_min: 10,
  alert_threshold_min: 15,
  live_monitoring_aktywny: 1
};

const LocationHoursModal = ({ locationId, locationName, onClose, onSaved }) => {
  const [rows, setRows] = useState(DEFAULT_ROWS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [punkt, setPunkt] = useState(DEFAULT_PUNKT);
  const [punktLoading, setPunktLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setPunktLoading(true);
      try {
        const res = await grafikService.getLocationPunktualnoscConfig(locationId);
        if (cancelled) return;
        const data = res?.data || res || {};
        setPunkt({
          tolerancja_spoznienie_min: Number(data.tolerancja_spoznienie_min ?? DEFAULT_PUNKT.tolerancja_spoznienie_min),
          tolerancja_wczesniej_min: Number(data.tolerancja_wczesniej_min ?? DEFAULT_PUNKT.tolerancja_wczesniej_min),
          alert_threshold_min: Number(data.alert_threshold_min ?? DEFAULT_PUNKT.alert_threshold_min),
          live_monitoring_aktywny: data.live_monitoring_aktywny != null
            ? (Number(data.live_monitoring_aktywny) ? 1 : 0)
            : DEFAULT_PUNKT.live_monitoring_aktywny
        });
      } catch (err) {
        // niemy fallback do defaultow - moze backend jeszcze nie wystawia
        if (!cancelled) setPunkt(DEFAULT_PUNKT);
      } finally {
        if (!cancelled) setPunktLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [locationId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await grafikService.getLocationHours(locationId);
        // res ksztalt: {success, data: [...], message, status_code}
        const list = Array.isArray(res?.data) ? res.data
                   : Array.isArray(res?.data?.data) ? res.data.data
                   : Array.isArray(res) ? res
                   : [];
        if (cancelled) return;
        if (!list || list.length === 0) {
          setRows(DEFAULT_ROWS);
        } else {
          // Mapowanie po dzien_tygodnia (dolozenie brakujacych dni)
          const byDay = {};
          list.forEach(r => { byDay[Number(r.dzien_tygodnia)] = r; });
          setRows(DAYS.map(d => {
            const r = byDay[d.dt];
            return {
              dzien_tygodnia: d.dt,
              godzina_od: (r?.godzina_od || '').slice(0, 5),
              godzina_do: (r?.godzina_do || '').slice(0, 5),
              zamkniete: !!r?.zamkniete
            };
          }));
        }
      } catch (err) {
        toast.error(err.message || 'Blad pobierania godzin pracy');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [locationId]);

  const updateRow = (dt, field, value) => {
    setRows(prev => prev.map(r => r.dzien_tygodnia === dt ? { ...r, [field]: value } : r));
  };

  const applyToAllWeekdays = () => {
    const monday = rows.find(r => r.dzien_tygodnia === 1);
    if (!monday || !monday.godzina_od || !monday.godzina_do) {
      toast.error('Najpierw uzupelnij godziny dla poniedzialku');
      return;
    }
    setRows(prev => prev.map(r =>
      r.dzien_tygodnia >= 2 && r.dzien_tygodnia <= 5
        ? { ...r, godzina_od: monday.godzina_od, godzina_do: monday.godzina_do, zamkniete: false }
        : r
    ));
    toast.success('Godziny z poniedzialku skopiowane na wt-pt');
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Zamkniete dni: wysylamy null zamiast czasu
      const payload = rows.map(r => ({
        dzien_tygodnia: r.dzien_tygodnia,
        godzina_od: r.zamkniete ? null : (r.godzina_od || null),
        godzina_do: r.zamkniete ? null : (r.godzina_do || null),
        zamkniete: r.zamkniete ? 1 : 0
      }));
      await grafikService.updateLocationHours(locationId, payload);

      // Punktualnosc - prubujemy zapisac, ale nie blokujemy zapisu godzin
      try {
        await grafikService.updateLocationPunktualnoscConfig(locationId, {
          tolerancja_spoznienie_min: Number(punkt.tolerancja_spoznienie_min) || 0,
          tolerancja_wczesniej_min: Number(punkt.tolerancja_wczesniej_min) || 0,
          alert_threshold_min: Number(punkt.alert_threshold_min) || 0,
          live_monitoring_aktywny: punkt.live_monitoring_aktywny ? 1 : 0
        });
      } catch (errCfg) {
        toast.error(errCfg.message || 'Konfiguracja punktualnosci nie zostala zapisana');
      }

      toast.success('Godziny pracy zapisane');
      onSaved && onSaved();
      onClose && onClose();
    } catch (err) {
      toast.error(err.message || 'Blad zapisu godzin pracy');
    } finally {
      setSaving(false);
    }
  };

  const updatePunkt = (field, value) => {
    setPunkt(prev => ({ ...prev, [field]: value }));
  };

  const labelStyle = { display: 'block', fontSize: '0.75rem', fontWeight: 600, marginBottom: '0.25rem', color: 'var(--text-muted)' };
  const inputStyle = {
    width: '100%', padding: '0.4rem 0.5rem',
    border: '1px solid var(--border)', borderRadius: '0.375rem',
    backgroundColor: 'var(--card-bg)', color: 'var(--text)',
    fontSize: '0.85rem'
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)',
      zIndex: 1050, display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: '1rem'
    }}>
      <div style={{
        backgroundColor: 'var(--card-bg)', borderRadius: '0.75rem',
        maxWidth: '700px', width: '100%', maxHeight: '90vh', overflowY: 'auto',
        boxShadow: '0 10px 40px rgba(0,0,0,0.3)'
      }}>
        <div style={{
          padding: '1rem 1.5rem', borderBottom: '1px solid var(--border)',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          background: 'linear-gradient(135deg, var(--info) 0%, var(--info) 100%)',
          color: '#fff', borderRadius: '0.75rem 0.75rem 0 0'
        }}>
          <h5 style={{ margin: 0, fontWeight: 600 }}>
            <i className="fas fa-clock" style={{ marginRight: '0.5rem' }}></i>
            Domyslne godziny pracy: {locationName || `lokalizacja #${locationId}`}
          </h5>
          <button
            onClick={onClose}
            style={{
              background: 'transparent', border: 'none', color: '#fff',
              fontSize: '1.5rem', cursor: 'pointer', padding: 0, lineHeight: 1
            }}
          >&times;</button>
        </div>

        <div style={{ padding: '1.5rem' }}>
          <div style={{
            backgroundColor: 'var(--bg)', borderRadius: '0.375rem', padding: '0.5rem 0.75rem',
            marginBottom: '1rem', fontSize: '0.8rem', color: 'var(--text-muted)'
          }}>
            <i className="fas fa-info-circle" style={{ marginRight: '0.4rem', color: 'var(--info)' }}></i>
            Te godziny beda automatycznie pre-fillowane przy tworzeniu nowej zmiany w grafiku
            dla tej lokalizacji.
          </div>

          {loading ? (
            <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-muted)' }}>
              Ladowanie godzin...
            </div>
          ) : (
            <>
              <div style={{ marginBottom: '0.75rem' }}>
                <button
                  onClick={applyToAllWeekdays}
                  style={{
                    padding: '4px 10px', fontSize: '11px',
                    border: '1px solid var(--info)',
                    backgroundColor: 'transparent', color: 'var(--info)',
                    cursor: 'pointer', fontWeight: 500
                  }}
                >
                  <i className="fas fa-copy" style={{ marginRight: '0.4rem' }}></i>
                  Skopiuj poniedzialek na wt-pt
                </button>
              </div>

              <div style={{
                display: 'grid',
                gridTemplateColumns: '120px 1fr 1fr 90px',
                gap: '0.5rem 0.75rem',
                alignItems: 'end',
                paddingBottom: '0.5rem',
                marginBottom: '0.5rem',
                borderBottom: '1px solid var(--border)'
              }}>
                <div style={labelStyle}>Dzien</div>
                <div style={labelStyle}>Otwarcie</div>
                <div style={labelStyle}>Zamkniecie</div>
                <div style={labelStyle}>Zamkniete</div>
              </div>

              {rows.map(r => {
                const day = DAYS.find(d => d.dt === r.dzien_tygodnia);
                return (
                  <div
                    key={r.dzien_tygodnia}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '120px 1fr 1fr 90px',
                      gap: '0.5rem 0.75rem',
                      alignItems: 'center',
                      padding: '0.4rem 0',
                      opacity: r.zamkniete ? 0.5 : 1
                    }}
                  >
                    <div style={{ fontWeight: 600, fontSize: '0.85rem' }}>{day?.label}</div>
                    <input
                      type="time"
                      value={r.godzina_od}
                      disabled={r.zamkniete}
                      onChange={(e) => updateRow(r.dzien_tygodnia, 'godzina_od', e.target.value)}
                      style={inputStyle}
                    />
                    <input
                      type="time"
                      value={r.godzina_do}
                      disabled={r.zamkniete}
                      onChange={(e) => updateRow(r.dzien_tygodnia, 'godzina_do', e.target.value)}
                      style={inputStyle}
                    />
                    <label style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.4rem', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={r.zamkniete}
                        onChange={(e) => updateRow(r.dzien_tygodnia, 'zamkniete', e.target.checked)}
                        style={{ cursor: 'pointer' }}
                      />
                    </label>
                  </div>
                );
              })}
            </>
          )}

          {/* Sekcja: Punktualnosc */}
          <div style={{
            marginTop: '1.5rem',
            paddingTop: '1rem',
            borderTop: '2px solid var(--border)'
          }}>
            <div style={{
              display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem'
            }}>
              <i className="fas fa-stopwatch" style={{ color: 'var(--info)' }} />
              <h6 style={{ margin: 0, fontSize: '0.95rem', fontWeight: 600, color: 'var(--text)' }}>
                Punktualnosc
              </h6>
            </div>

            {punktLoading ? (
              <div style={{ padding: '1rem', textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.85rem' }}>
                Ladowanie konfiguracji...
              </div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 100px', gap: '0.5rem 1rem', alignItems: 'center' }}>
                <label style={{ fontSize: '0.85rem', color: 'var(--text)' }}>
                  Tolerancja spoznienia (min):
                </label>
                <input
                  type="number"
                  min="0" max="120"
                  value={punkt.tolerancja_spoznienie_min}
                  onChange={(e) => updatePunkt('tolerancja_spoznienie_min', e.target.value)}
                  style={inputStyle}
                />

                <label style={{ fontSize: '0.85rem', color: 'var(--text)' }}>
                  Tolerancja wczesniejszego wyjscia (min):
                </label>
                <input
                  type="number"
                  min="0" max="120"
                  value={punkt.tolerancja_wczesniej_min}
                  onChange={(e) => updatePunkt('tolerancja_wczesniej_min', e.target.value)}
                  style={inputStyle}
                />

                <label style={{ fontSize: '0.85rem', color: 'var(--text)' }}>
                  Prog alertu (min):
                  <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: '0.1rem' }}>
                    Powyzej tej wartosci admini dostaja powiadomienie.
                  </div>
                </label>
                <input
                  type="number"
                  min="0" max="240"
                  value={punkt.alert_threshold_min}
                  onChange={(e) => updatePunkt('alert_threshold_min', e.target.value)}
                  style={inputStyle}
                />

                <label style={{ fontSize: '0.85rem', color: 'var(--text)', display: 'flex', alignItems: 'center', gap: '0.5rem', gridColumn: '1 / span 2', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={!!punkt.live_monitoring_aktywny}
                    onChange={(e) => updatePunkt('live_monitoring_aktywny', e.target.checked ? 1 : 0)}
                    style={{ cursor: 'pointer' }}
                  />
                  <span>
                    <strong>Live monitoring aktywny</strong>
                    <span style={{ color: 'var(--text-muted)', marginLeft: '0.4rem' }}>
                      Pokazuj te lokalizacje w panelu "Live Status".
                    </span>
                  </span>
                </label>
              </div>
            )}
          </div>
        </div>

        <div style={{
          padding: '1rem 1.5rem', borderTop: '1px solid var(--border)',
          display: 'flex', justifyContent: 'flex-end', gap: '0.5rem',
          backgroundColor: 'var(--bg)', borderRadius: '0 0 0.75rem 0.75rem'
        }}>
          <button
            onClick={onClose}
            disabled={saving}
            style={{
              padding: '6px 12px', fontSize: '12px',
              border: '1px solid var(--border)',
              backgroundColor: 'var(--card-bg)', color: 'var(--text)',
              cursor: 'pointer', fontWeight: 500
            }}
          >Anuluj</button>
          <button
            onClick={handleSave}
            disabled={saving || loading}
            style={{
              padding: '6px 12px', fontSize: '12px',
              border: 'none',
              background: 'linear-gradient(135deg, var(--success) 0%, var(--success) 100%)',
              color: '#fff', cursor: 'pointer', fontWeight: 600
            }}
          >
            <i className="fas fa-save" style={{ marginRight: '0.4rem' }}></i>
            {saving ? 'Zapisywanie...' : 'Zapisz godziny'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default LocationHoursModal;
